// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';

import { smoothPath } from './smoothPath';

export interface MetricSparklineProps {
  data: number[];
  tone?: 'primary' | 'amber'; // used only when no thresholds given (existing behavior)
  domainMax?: number; // value mapped to the top of the chart; default = peak of data
  thresholds?: { warn?: number; crit?: number }; // value thresholds in same units as data
  /** Draw a faint dashed reference line at this value (e.g. the SLO target / critical limit). */
  referenceValue?: number;
  /** Optional tiny label shown at the right end of the reference line, e.g. "limit · 13/s". */
  referenceLabel?: string;
  /**
   * For activity/throughput charts where zero means "no activity" (not "healthy"):
   * color zero-value points neutral grey instead of green. Leave off for health
   * metrics where low/zero is the ideal state (an empty queue stays green).
   */
  idleAtZero?: boolean;
  /**
   * When true, draw a Y scale and (if `pointLabels` is set) sparse X labels.
   * Opt-in so compact call sites (audit strip, internal-health) stay dense.
   */
  showAxes?: boolean;
  /** Per-point labels parallel to `data` — used for X ticks and hover tooltips. */
  pointLabels?: string[];
  /** Formats the Y tick and tooltip value; default is the raw number. */
  formatValue?: (value: number) => string;
  className?: string;
  ariaLabel?: string;
}

const W = 300;
const H_COMPACT = 40;
const H_AXES = 72;
const PAD = 4;
const AXIS_LEFT = 28;
const AXIS_BOTTOM = 16;
// Moderate smoothing — rounded enough to look refined, but not so much that
// spikes flatten into sine-like hills. 0 = straight polyline, 1 = full
// Catmull-Rom; ~0.65 sits between "too spiky" (0.4) and "too smooth" (1).
const SMOOTHING = 0.65;

// Slightly softened for a refined line (red stays full — it's the alert).
const STATUS_GREEN = 'hsl(var(--status-green) / 0.88)';
const STATUS_AMBER = 'hsl(var(--status-amber) / 0.8)';
const STATUS_RED = 'hsl(var(--status-red))';
// "No activity" color: the green hue at LOW alpha → a faint, desaturated green.
// Same hue as the active green, so the idle→active transition is a pure
// intensity/alpha ramp (no hue crossing) — idle reads as a ghosted line that
// solidifies into full green when activity resumes. Re-themes via the token.
const STATUS_IDLE = 'hsl(var(--status-green) / 0.3)';

/**
 * Returns the semantic color token for the band a given value falls in.
 * Normal (below warn) = green — the healthy baseline for threshold charts.
 * Breach warms amber → red. When `idleColor` is given, a zero value (no activity)
 * returns that neutral color instead of green.
 * Exported for reuse (e.g. coloring head-dots or legend chips).
 */
export function bandColor(
  v: number,
  thresholds: { warn?: number; crit?: number },
  idleColor?: string,
): string {
  const { warn, crit } = thresholds;
  if (idleColor !== undefined && v <= 0) return idleColor;
  if (crit !== undefined && v >= crit) return STATUS_RED;
  if (warn !== undefined && v >= warn) return STATUS_AMBER;
  return STATUS_GREEN;
}

interface GradStop {
  offset: string;
  color: string;
  opacity: number;
}

/**
 * Builds heat-map stops along the TIME (x) axis — one stop per data point at its
 * x-position, colored by that point's value band. The gradient is horizontal, so
 * each vertical slice (a moment in time) is a single uniform color set by the
 * value there: a breaching section reads as a solid red column, sloping down
 * through amber to the calm green baseline at rest. SVG interpolates between
 * adjacent points for a smooth left-to-right ramp.
 */
function buildHeatStops(
  data: number[],
  warn: number | undefined,
  crit: number | undefined,
  opacity: number,
  idleColor?: string,
): GradStop[] {
  const n = data.length;
  const idleOn = idleColor !== undefined;
  const isIdle = (v: number): boolean => idleOn && v <= 0;
  const stops: GradStop[] = [];
  for (let i = 0; i < n; i++) {
    const v = data[i] ?? 0;
    const off = (i / (n - 1)) * 100;
    const color = bandColor(v, { warn, crit }, idleColor);
    stops.push({ offset: `${off.toFixed(2)}%`, color, opacity });

    // At an idle↔active boundary, hold the current color until just before the
    // next point so the flip happens over a few pixels (a quick fade) rather than
    // blending across the whole interval. Value-band (green/amber/red) transitions
    // between two active points are left to interpolate smoothly.
    const next = data[i + 1];
    if (idleOn && next !== undefined && isIdle(v) !== isIdle(next)) {
      const nextOff = ((i + 1) / (n - 1)) * 100;
      const edge = nextOff - 0.6;
      if (edge > off) stops.push({ offset: `${edge.toFixed(2)}%`, color, opacity });
    }
  }
  return stops;
}

function defaultFormat(v: number): string {
  return String(v);
}

/** Pick up to three X-label indices: first, middle, last (deduped for short series). */
function xLabelIndices(n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [0];
  if (n === 2) return [0, 1];
  const mid = Math.floor((n - 1) / 2);
  return [...new Set([0, mid, n - 1])];
}

export function MetricSparkline({
  data,
  tone = 'primary',
  domainMax,
  thresholds,
  referenceValue,
  referenceLabel,
  idleAtZero,
  showAxes = false,
  pointLabels,
  formatValue = defaultFormat,
  className,
  ariaLabel,
}: MetricSparklineProps): React.JSX.Element | null {
  const gradId = React.useId();
  const strokeGradId = `${gradId}-stroke`;
  const areaGradId = `${gradId}-area`;
  const fadeGradId = `${gradId}-fade`;
  const fadeMaskId = `${gradId}-mask`;
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const tooltipId = `${gradId}-tip`;
  const [hover, setHover] = React.useState<{
    index: number;
    clientX: number;
    clientY: number;
  } | null>(null);

  if (data.length < 2) return null;

  const H = showAxes ? H_AXES : H_COMPACT;
  const plotLeft = showAxes ? AXIS_LEFT : 0;
  const plotRight = showAxes ? W - PAD : W;
  const plotTop = PAD;
  const plotBottom = showAxes ? H - AXIS_BOTTOM : H - PAD;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const peak = data.reduce((m, v) => (v > m ? v : m), 0);
  const max = domainMax != null && domainMax > 0 ? domainMax : peak > 0 ? peak : 1;

  const yOf = (v: number): number => plotBottom - (Math.min(v, max) / max) * plotH;
  const xOf = (i: number): number => plotLeft + (i / (data.length - 1)) * plotW;

  const pts = data.map((v, i) => ({ x: xOf(i), y: yOf(v) }));

  const line = smoothPath(pts, SMOOTHING);
  const area = `${line} L${plotRight},${plotBottom} L${plotLeft},${plotBottom} Z`;
  const head = pts[pts.length - 1];
  const lastValue = data[data.length - 1] ?? 0;

  const hasBanding =
    thresholds !== undefined && (thresholds.warn !== undefined || thresholds.crit !== undefined);

  const toneColor = tone === 'amber' ? STATUS_AMBER : 'hsl(var(--primary))';

  const rawWarn = thresholds?.warn;
  const rawCrit = thresholds?.crit;
  const [normWarn, normCrit] =
    rawWarn !== undefined && rawCrit !== undefined && rawWarn > rawCrit
      ? [rawCrit, rawWarn]
      : [rawWarn, rawCrit];

  const idleColor = idleAtZero ? STATUS_IDLE : undefined;
  const strokeStops = hasBanding ? buildHeatStops(data, normWarn, normCrit, 1, idleColor) : [];
  const areaStops = hasBanding ? buildHeatStops(data, normWarn, normCrit, 0.18, idleColor) : [];

  const headColor = hasBanding
    ? bandColor(lastValue, { warn: normWarn, crit: normCrit }, idleColor)
    : toneColor;

  const yTicks = showAxes
    ? [
        { v: max, y: yOf(max) },
        { v: 0, y: yOf(0) },
      ]
    : [];

  const onMove = (e: { currentTarget: SVGSVGElement; clientX: number; clientY: number }): void => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    if (rect.width <= 0) return;
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const d = Math.abs((pts[i]?.x ?? 0) - svgX);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHover({ index: best, clientX: e.clientX, clientY: e.clientY });
  };

  const hoverAt = (index: number, svg: SVGSVGElement): void => {
    const rect = svg.getBoundingClientRect();
    const pt = pts[index];
    if (pt === undefined || rect.width <= 0) return;
    setHover({
      index,
      clientX: rect.left + (pt.x / W) * rect.width,
      clientY: rect.top + (pt.y / H) * rect.height,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<SVGSVGElement>): void => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const last = pts.length - 1;
    const current = hover?.index ?? (e.key === 'ArrowRight' ? -1 : pts.length);
    const next = e.key === 'ArrowRight' ? Math.min(current + 1, last) : Math.max(current - 1, 0);
    hoverAt(next, e.currentTarget);
  };

  const hoverValue = hover !== null ? (data[hover.index] ?? 0) : 0;
  const hoverLabel =
    hover !== null && pointLabels !== undefined && pointLabels.length === data.length
      ? pointLabels[hover.index]
      : undefined;

  let tooltipStyle: React.CSSProperties | undefined;
  if (hover !== null && wrapRef.current) {
    const wrap = wrapRef.current.getBoundingClientRect();
    tooltipStyle = {
      left: Math.min(Math.max(8, hover.clientX - wrap.left + 12), wrap.width - 8),
      top: Math.max(4, hover.clientY - wrap.top - 36),
    };
  }

  return (
    <div
      ref={wrapRef}
      className={cn('relative w-full', showAxes ? 'h-20' : 'h-8', className)}
      data-testid="metric-sparkline"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block h-full w-full overflow-visible"
        role={ariaLabel ? 'img' : undefined}
        aria-hidden={ariaLabel ? undefined : true}
        aria-label={ariaLabel}
        aria-describedby={ariaLabel && hover !== null ? tooltipId : undefined}
        tabIndex={ariaLabel ? 0 : undefined}
        onPointerMove={onMove}
        onMouseMove={onMove}
        onPointerLeave={() => setHover(null)}
        onMouseLeave={() => setHover(null)}
        onFocus={(e) => hoverAt(pts.length - 1, e.currentTarget)}
        onBlur={() => setHover(null)}
        onKeyDown={onKeyDown}
      >
        <defs>
          {hasBanding ? (
            <>
              <linearGradient
                id={strokeGradId}
                gradientUnits="userSpaceOnUse"
                x1={plotLeft}
                y1="0"
                x2={plotRight}
                y2="0"
              >
                {strokeStops.map((s, i) => (
                  <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
                ))}
              </linearGradient>
              <linearGradient
                id={areaGradId}
                gradientUnits="userSpaceOnUse"
                x1={plotLeft}
                y1="0"
                x2={plotRight}
                y2="0"
              >
                {areaStops.map((s, i) => (
                  <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={s.opacity} />
                ))}
              </linearGradient>
              <linearGradient id={fadeGradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fff" stopOpacity="1" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0" />
              </linearGradient>
              <mask id={fadeMaskId}>
                <rect
                  x={plotLeft}
                  y={plotTop}
                  width={plotW}
                  height={plotH}
                  fill={`url(#${fadeGradId})`}
                />
              </mask>
            </>
          ) : (
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={toneColor} stopOpacity="0.18" />
              <stop offset="100%" stopColor={toneColor} stopOpacity="0" />
            </linearGradient>
          )}
        </defs>
        <line
          x1={plotLeft}
          y1={plotBottom}
          x2={plotRight}
          y2={plotBottom}
          stroke="hsl(var(--border))"
          strokeWidth="1"
        />
        {yTicks.map((t) => (
          <line
            key={`y-${t.v}`}
            x1={plotLeft}
            y1={t.y}
            x2={plotRight}
            y2={t.y}
            stroke="hsl(var(--border))"
            strokeWidth="1"
            strokeDasharray={t.v === 0 ? undefined : '2 3'}
            opacity={t.v === 0 ? 1 : 0.45}
          />
        ))}
        <path
          d={area}
          fill={hasBanding ? `url(#${areaGradId})` : `url(#${gradId})`}
          mask={hasBanding ? `url(#${fadeMaskId})` : undefined}
        />
        {referenceValue != null && referenceValue > 0 && (
          <line
            x1={plotLeft}
            y1={yOf(referenceValue)}
            x2={plotRight}
            y2={yOf(referenceValue)}
            stroke="hsl(var(--slate))"
            strokeWidth="1"
            strokeDasharray="2 3"
            opacity="0.45"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <path
          d={line}
          fill="none"
          stroke={hasBanding ? `url(#${strokeGradId})` : toneColor}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {head && (
          <path
            d={`M ${head.x.toFixed(1)} ${head.y.toFixed(1)} l 0.01 0`}
            stroke={headColor}
            strokeWidth="6"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {hover !== null && pts[hover.index] && (
          <path
            d={`M ${pts[hover.index]!.x.toFixed(1)} ${pts[hover.index]!.y.toFixed(1)} l 0.01 0`}
            stroke="hsl(var(--foreground))"
            strokeWidth="7"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
        )}
      </svg>
      {yTicks.map((t) => (
        <span
          key={`y-${t.v}`}
          aria-hidden="true"
          className="pointer-events-none absolute font-mono text-[9px] tabular-nums text-muted-foreground"
          style={{
            left: `${((plotLeft - 4) / W) * 100}%`,
            top: `${(t.y / H) * 100}%`,
            transform: 'translate(-100%, -50%)',
          }}
        >
          {formatValue(t.v)}
        </span>
      ))}
      {showAxes &&
        pointLabels !== undefined &&
        pointLabels.length === data.length &&
        xLabelIndices(data.length).map((i) => (
          <span
            key={`x-${i}`}
            aria-hidden="true"
            className="pointer-events-none absolute text-[9px] text-muted-foreground"
            style={{
              left: `${(xOf(i) / W) * 100}%`,
              top: `${((H - 4) / H) * 100}%`,
              transform:
                i === 0
                  ? 'translateY(-50%)'
                  : i === data.length - 1
                    ? 'translate(-100%, -50%)'
                    : 'translate(-50%, -50%)',
            }}
          >
            {pointLabels[i]}
          </span>
        ))}
      {referenceLabel != null && referenceValue != null && referenceValue > 0 && (
        <span
          className="pointer-events-none absolute right-1 z-10 font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground"
          style={{
            top: `${(yOf(referenceValue) / H) * 100}%`,
            transform: 'translateY(-115%)',
          }}
        >
          {referenceLabel}
        </span>
      )}
      {hover !== null && tooltipStyle && (
        <div
          id={tooltipId}
          role="tooltip"
          data-testid="metric-sparkline-tooltip"
          className="pointer-events-none absolute z-20 -translate-x-1/2 rounded-gl border border-border bg-popover px-2 py-1 text-[11px] text-popover-foreground shadow-sm"
          style={tooltipStyle}
        >
          {hoverLabel !== undefined && <div className="text-muted-foreground">{hoverLabel}</div>}
          <div className="font-mono tabular-nums">{formatValue(hoverValue)}</div>
        </div>
      )}
    </div>
  );
}
